import React, { useEffect, useMemo, useState } from 'react';
import JsonQuery from 'json-query';
import { isValid } from 'date-fns';
import { CustomDatePicker } from '../CustomDatePicker';
import { useLocalization } from '../../hooks';
import { DateFilterOptions } from './';

interface DateFilterProps {
    data: any;
    onChangeDateRange: (minDate: any, maxDate: any) => void;
    staticDataTabIndex?: number;
}

const CustomDateFilter: React.FC<DateFilterOptions & DateFilterProps> = (props) => {
    const { data, startDatelabel, endDatelabel, ...rest } = props;
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [minDate, setMinDate] = useState<any>(null);
    const [maxDate, setMaxDate] = useState<any>(null);
    const i18n = useLocalization();

    // Extract the dates from the provided data using json-query
    const dates = useMemo(
        () => JsonQuery(rest?.dataKey || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.dataKey]
    );

    const limitedDates = useMemo(
        () => JsonQuery(rest?.limitDatekeySelection || '', { data }).value || [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rest?.limitDatekeySelection]
    );

    // Update the start and end date when the dates data changes
    useEffect(() => {
        if (dates?.length > 0) {
            let minDate = new Date(dates?.[0]);
            let maxDate = new Date(dates?.[0]);

            dates?.forEach((date: string) => {
                const currentDate = new Date(date);
                if (isValid(currentDate)) {
                    if (currentDate < minDate) {
                        minDate = currentDate;
                    }
                    if (currentDate > maxDate) {
                        maxDate = currentDate;
                    }
                }
            });

            if (isValid(minDate) && isValid(maxDate)) {
                setMinDate(minDate);
                setStartDate(minDate);
                setMaxDate(maxDate);
                setEndDate(maxDate);
            }
        }
    }, [dates]);

    // Call the onChangeDateRange callback when the start or end date changes
    useEffect(() => {
        if (
            startDate &&
            isValid(startDate) &&
            endDate &&
            isValid(endDate) &&
            !(startDate > endDate)
        ) {
            rest.onChangeDateRange(startDate, endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    useEffect(() => {
        if (rest?.clearFilters) {
            setMinDate('');
            setStartDate('');
            setMaxDate('');
            setEndDate('');
            rest.onChangeDateRange('', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest?.clearFilters]);

    return (
        <div className="date-filter-container">
            <div className="start-date-container">
                <span tabIndex={rest.staticDataTabIndex}>{i18n.format(startDatelabel, data)}</span>
                <CustomDatePicker
                    {...rest}
                    id="startDateInput"
                    minDate={rest.limitStartDateMinToMinDate ? minDate : null}
                    maxDate={
                        rest.limitStartDateMaxToCurrentDate
                            ? new Date()
                            : rest?.limitStartDateMaxToMaxDate
                              ? maxDate
                              : null
                    }
                    selected={startDate}
                    onChange={(date: any) => setStartDate(date)}
                    startDate={startDate}
                    endDate={endDate}
                    isStartDate={true}
                    startDateErrorMessage={i18n.format(rest?.startDateErrorMessage, data)}
                    closeOnScroll
                    formatWeekDay={rest.numberOfLettersForTheDays as any}
                    limitedDates={limitedDates}
                    limitDateStartErrorMessage={i18n.format(rest?.limitDateStartErrorMessage, data)}
                    clearDates={rest?.clearFilters}
                />
            </div>
            <div className="end-date-container">
                <span tabIndex={rest.staticDataTabIndex}>{i18n.format(endDatelabel, data)}</span>
                <CustomDatePicker
                    {...rest}
                    id="endDateInput"
                    selected={endDate}
                    minDate={rest.limitEndDateMinToStartDate ? startDate : null}
                    maxDate={
                        rest?.limitEndDateMaxToCurrentDate
                            ? new Date()
                            : rest?.limitEndDateMaxToMaxDate
                              ? maxDate
                              : null
                    }
                    onChange={(date: any) => setEndDate(date)}
                    isEndDate={true}
                    endDate={endDate}
                    startDate={startDate}
                    endDateErrorMessage={i18n.format(rest?.endDateErrorMessage, data)}
                    closeOnScroll
                    formatWeekDay={rest.numberOfLettersForTheDays as any}
                    limitedDates={limitedDates}
                    limitDateEndErrorMessage={i18n.format(rest?.limitDateEndErrorMessage, data)}
                    clearDates={rest?.clearFilters}
                />
            </div>
        </div>
    );
};

export default CustomDateFilter;
